/**
 * Utility functions for handling research field operations
 */

/**
 * Converts a field name to a photo URL for the field image
 * @param {string} field - The research field name
 * @returns {string} The photo URL path
 */
export const getPhotoURL = (field) => {
  if (!field) return "/field-images/default.jpg";

  // Convert field name to lowercase and replace spaces with hyphens
  const convertedField = field
    .toLowerCase()
    .replace(/\s+/g, "-")
    .replace(/[^a-z0-9-]/g, ""); // Remove any special characters except hyphens

  return `/field-images/${convertedField}.jpg`;
};

/**
 * Get all available field names (matching the available image files)
 * @returns {Array} Array of field names
 */
export const getAvailableFields = () => [
  "Anthropology",
  "Art",
  "Astronomy",
  "Biology",
  "Chemistry",
  "Computer Science",
  "Economics",
  "Engineering",
  "Environmental Science",
  "Geography",
  "History",
  "Literature",
  "Mathematics",
  "Medicine",
  "Music",
  "Philosophy",
  "Physics",
  "Political Science",
  "Psychology",
  "Sociology",
];

/**
 * Get comprehensive subfields for each research field
 * @returns {Object} Object mapping field names to their subfields
 */
export const getFieldSubFields = () => ({
  Anthropology: [
    "Cultural Anthropology",
    "Biological Anthropology",
    "Archaeological Anthropology",
    "Linguistic Anthropology",
    "Applied Anthropology",
    "Medical Anthropology",
    "Urban Anthropology",
    "Environmental Anthropology",
  ],
  Art: [
    "Fine Arts",
    "Digital Art",
    "Art History",
    "Visual Arts",
    "Contemporary Art",
    "Classical Art",
    "Art Therapy",
    "Art Education",
  ],
  Astronomy: [
    "Astrophysics",
    "Planetary Science",
    "Cosmology",
    "Stellar Astronomy",
    "Galactic Astronomy",
    "Radio Astronomy",
    "Observational Astronomy",
    "Theoretical Astronomy",
  ],
  Biology: [
    "Genetics",
    "Microbiology",
    "Ecology",
    "Molecular Biology",
    "Cell Biology",
    "Evolutionary Biology",
    "Marine Biology",
    "Botany",
    "Zoology",
    "Biochemistry",
  ],
  Chemistry: [
    "Organic Chemistry",
    "Inorganic Chemistry",
    "Biochemistry",
    "Physical Chemistry",
    "Analytical Chemistry",
    "Medicinal Chemistry",
    "Environmental Chemistry",
    "Materials Chemistry",
  ],
  "Computer Science": [
    "Artificial Intelligence",
    "Machine Learning",
    "Data Science",
    "Computer Vision",
    "Software Engineering",
    "Cybersecurity",
    "Human-Computer Interaction",
    "Database Systems",
    "Computer Networks",
    "Algorithms",
  ],
  Economics: [
    "Microeconomics",
    "Macroeconomics",
    "Behavioral Economics",
    "Development Economics",
    "International Economics",
    "Financial Economics",
    "Labor Economics",
    "Environmental Economics",
  ],
  Engineering: [
    "Mechanical Engineering",
    "Electrical Engineering",
    "Civil Engineering",
    "Chemical Engineering",
    "Biomedical Engineering",
    "Software Engineering",
    "Aerospace Engineering",
    "Environmental Engineering",
    "Industrial Engineering",
  ],
  "Environmental Science": [
    "Climate Science",
    "Ecology",
    "Environmental Chemistry",
    "Conservation Biology",
    "Environmental Policy",
    "Renewable Energy",
    "Pollution Control",
    "Sustainability Studies",
  ],
  Geography: [
    "Physical Geography",
    "Human Geography",
    "Cartography",
    "Geographic Information Systems",
    "Urban Geography",
    "Economic Geography",
    "Cultural Geography",
    "Environmental Geography",
  ],
  History: [
    "Ancient History",
    "Medieval History",
    "Modern History",
    "Social History",
    "Cultural History",
    "Political History",
    "Economic History",
    "Military History",
  ],
  Literature: [
    "Comparative Literature",
    "English Literature",
    "World Literature",
    "Literary Theory",
    "Creative Writing",
    "Poetry",
    "Drama",
    "Fiction Studies",
  ],
  Mathematics: [
    "Algebra",
    "Calculus",
    "Statistics",
    "Geometry",
    "Number Theory",
    "Applied Mathematics",
    "Pure Mathematics",
    "Mathematical Analysis",
    "Discrete Mathematics",
  ],
  Medicine: [
    "Internal Medicine",
    "Surgery",
    "Pediatrics",
    "Cardiology",
    "Neurology",
    "Oncology",
    "Psychiatry",
    "Radiology",
    "Emergency Medicine",
    "Public Health",
  ],
  Music: [
    "Music Theory",
    "Music Composition",
    "Music Performance",
    "Music Education",
    "Ethnomusicology",
    "Music Technology",
    "Music History",
    "Music Therapy",
  ],
  Philosophy: [
    "Ethics",
    "Metaphysics",
    "Logic",
    "Epistemology",
    "Political Philosophy",
    "Philosophy of Mind",
    "Philosophy of Science",
    "Aesthetics",
  ],
  Physics: [
    "Quantum Physics",
    "Astrophysics",
    "Nuclear Physics",
    "Thermodynamics",
    "Particle Physics",
    "Condensed Matter Physics",
    "Optics",
    "Theoretical Physics",
  ],
  "Political Science": [
    "International Relations",
    "Comparative Politics",
    "Political Theory",
    "Public Policy",
    "American Politics",
    "Political Economy",
    "Public Administration",
    "Constitutional Law",
  ],
  Psychology: [
    "Clinical Psychology",
    "Cognitive Psychology",
    "Social Psychology",
    "Developmental Psychology",
    "Experimental Psychology",
    "Neuropsychology",
    "Educational Psychology",
    "Health Psychology",
  ],
  Sociology: [
    "Social Theory",
    "Urban Sociology",
    "Medical Sociology",
    "Economic Sociology",
    "Political Sociology",
    "Cultural Sociology",
    "Environmental Sociology",
    "Digital Sociology",
  ],
});

/**
 * Get the converted field name (for file naming purposes)
 * @param {string} field - The research field name
 * @returns {string} The converted field name
 */
export const getConvertedFieldName = (field) => {
  if (!field) return "default";

  return field
    .toLowerCase()
    .replace(/\s+/g, "-")
    .replace(/[^a-z0-9-]/g, "");
};
